<script lang="ts" module>
  // A group mixing icon and icon-less rows sets --settings-row-icon-col to align labels.
  import type { LucideIcon } from '$lib/ui/icons';

  export interface SettingsRowControlProps {
    'aria-labelledby'?: string;
    'aria-describedby'?: string;
  }
</script>

<script lang="ts" generics="P extends SettingsRowControlProps">
  import type { Component } from 'svelte';

  interface Props {
    label: string;
    description?: string;
    icon?: LucideIcon;
    control: Component<P>;
    controlProps: P;
    id?: string;
    class?: string;
  }

  let {
    label,
    description,
    icon: Icon,
    control: Control,
    controlProps,
    id,
    class: klass = '',
  }: Props = $props();

  // Minted once, not reactive, so the aria-labelledby link stays put.
  const fallbackId = `vd-row-${Math.random().toString(36).slice(2, 9)}`;
  const rowId = $derived(id ?? fallbackId);
  const labelId = $derived(`${rowId}-label`);
  const descId = $derived(description ? `${rowId}-desc` : undefined);
</script>

<div class="vd-settings-row {klass}">
  <span class="vd-settings-row__icon" aria-hidden="true">
    {#if Icon}<Icon size={18} strokeWidth={1.75} focusable="false" />{/if}
  </span>
  <span class="vd-settings-row__copy">
    <span class="vd-settings-row__label" id={labelId}>{label}</span>
    {#if description}
      <span class="vd-settings-row__desc" id={descId}>{description}</span>
    {/if}
  </span>
  <span class="vd-settings-row__control">
    <Control {...controlProps} aria-labelledby={labelId} aria-describedby={descId} />
  </span>
</div>

<style lang="scss">
  :global(.vd-settings-row + .vd-settings-row) {
    border-top: 1px solid var(--line);
  }

  .vd-settings-row {
    display: grid;
    grid-template-columns: var(--settings-row-icon-col, auto) minmax(0, 1fr) auto;
    align-items: center;
    row-gap: 0.35rem;
    padding-block: 0.6rem;
    font-family: var(--font-ui);
  }

  /* No column gap: it would open beside an empty icon cell and indent the label. */
  .vd-settings-row__icon {
    display: inline-flex;
    color: var(--ink-mute);

    &:has(:global(svg)) {
      margin-inline-end: 0.75rem;
    }
  }

  .vd-settings-row__copy {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    min-width: 0;
  }

  .vd-settings-row__label {
    font-size: var(--text-body);
    color: var(--ink);
  }

  /* --ink-soft: a sentence to read, not a label. */
  .vd-settings-row__desc {
    font-size: var(--text-meta);
    color: var(--ink-soft);
    line-height: 1.4;
  }

  .vd-settings-row__control {
    display: flex;
    justify-content: flex-end;
    min-width: 0;
    max-width: 100%;
    margin-inline-start: 0.75rem;
  }

  @media (max-width: 460px) {
    .vd-settings-row {
      grid-template-columns: var(--settings-row-icon-col, auto) minmax(0, 1fr);
    }

    .vd-settings-row__control {
      grid-column: 2;
      justify-content: flex-start;
      margin-inline-start: 0;
    }
  }
</style>
